import React, { useEffect, useState, useRef } from "react";
import { Typography, IconButton } from "@mhome/ui";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeDownIcon from "@mui/icons-material/VolumeDown";
import NumberBar from "../common/number-bar";
import { SyncBadge } from "../../common/badges";
import { useComponentBackgroundColor } from "../../common/adaptive-theme-provider";

const VolumeMuteDetail1Widget = ({ widget, onValueUpdate, pattern }) => {
  const getComponentBgColor = useComponentBackgroundColor();
  const [v0, setV0] = useState({});
  const [volume, setVolume] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [w1, setW1] = useState(null); // mute state
  const [w2, setW2] = useState(null); // volume value
  const [minMax, setMinMax] = useState({ min: 0, max: 100 });
  const newValueRef = useRef(0);

  useEffect(() => {
    setV0(widget);

    // Handle mute state from $w1 (if present)
    if (widget?.items?.$w1) {
      setW1(widget.items.$w1);
      if (widget.items.$w1.value === "mute") {
        setIsMuted(true);
      } else {
        setIsMuted(false);
      }
    } else {
      setW1(null);
      setIsMuted(false);
    }

    // Handle volume value from $w2 (if present)
    if (widget?.items?.$w2) {
      setW2(widget.items.$w2);
      setMinMax({
        min: widget.items.$w2.min ?? 0,
        max: widget.items.$w2.max ?? 100,
      });
      const newVolume = Number(widget.items.$w2.value);
      const validVolume = isNaN(newVolume) ? 0 : newVolume;
      setVolume(validVolume);
      newValueRef.current = validVolume;
    } else {
      setW2(null);
      setVolume(0);
    }
  }, [widget]);

  const sendVolumeUpdate = (data) => {
    if (!w2) return; // Don't send update if volume widget doesn't exist

    const adjustedValue = Math.min(Math.max(data, minMax.min), minMax.max);
    const updatedWidget = {
      ...v0.items.$w2,
      value: adjustedValue,
    };
    onValueUpdate(updatedWidget);
  };

  const sendMuteUpdate = (muteState) => {
    if (!w1) return; // Don't send update if mute widget doesn't exist

    const updatedWidget = {
      ...v0.items.$w1,
      value: muteState ? "mute" : "unmute",
    };
    onValueUpdate(updatedWidget);
  };

  const updateVisualValue = (newValue) => {
    const adjustedValue = Math.min(Math.max(newValue, minMax.min), minMax.max);
    setVolume(adjustedValue);
    newValueRef.current = adjustedValue;
  };

  const handleVolumeChange = (newValue) => {
    if (!w2) return; // Don't handle volume changes if volume widget doesn't exist
    updateVisualValue(newValue);
  };

  const handleVolumeUpdate = (newValue) => {
    if (!w2) return; // Don't handle volume updates if volume widget doesn't exist
    sendVolumeUpdate(newValue);
  };

  const handleMuteToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!w1) return; // Don't handle mute toggle if mute widget doesn't exist
    if (pattern && pattern.endsWith("_R")) return;

    const newMuteState = !isMuted;
    setIsMuted(newMuteState);
    sendMuteUpdate(newMuteState);
  };

  // Determine which icon to show based on mute state and volume
  const VolumeIcon = isMuted
    ? VolumeOffIcon
    : volume === 0
    ? VolumeOffIcon
    : volume < 50
    ? VolumeDownIcon
    : VolumeUpIcon;

  const disabled = pattern && pattern.endsWith("_R");

  return (
    <div className="w-full flex flex-col gap-4">
      {pattern && pattern.endsWith("_W") && (
        <div className="flex justify-end">
          <SyncBadge />
        </div>
      )}

      {/* Volume Controls Section */}
      <div
        className="flex flex-col rounded-2xl px-3 py-2"
        style={{
          backgroundColor: getComponentBgColor("hsl(var(--card))"),
        }}
      >
        <Typography className="mb-3 font-bold">Volume {volume}</Typography>

        <div
          className={`flex items-center justify-center px-2 overflow-visible ${
            w1 && w2 ? "gap-4" : "gap-0"
          }`}
          style={{ height: "40px" }}
          onClick={(e) => e.stopPropagation()}
        >
          {w1 && (
            <IconButton
              onClick={handleMuteToggle}
              className="p-0 hover:bg-transparent"
              style={{
                color: "hsl(var(--primary))",
                cursor:
                  pattern && pattern.endsWith("_R") ? "default" : "pointer",
                opacity: pattern && pattern.endsWith("_R") ? 0.8 : 1,
              }}
              disabled={pattern && pattern.endsWith("_R")}
            >
              <VolumeIcon
                style={{ color: "hsl(var(--primary))", fontSize: "2rem" }}
              />
            </IconButton>
          )}

          {/* Volume slider - only show if $w2 exists */}
          {w2 && (
            <NumberBar
              value={volume}
              onChange={handleVolumeChange}
              onValueUpdate={handleVolumeUpdate}
              min={minMax.min}
              max={minMax.max}
              suffix=""
              disabled={false}
              sliderProps={{
                opacity: w1 && isMuted ? 0.5 : 1,
                transition: "opacity 0.2s ease",
                "& .MuiSlider-thumb": {
                  opacity: w1 && isMuted ? 0.7 : 1,
                },
                "& .MuiSlider-track": {
                  opacity: w1 && isMuted ? 0.5 : 1,
                },
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default VolumeMuteDetail1Widget;
